import React, { useEffect, useState } from 'react';
import {
  Switch, Route, useHistory, useLocation,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Provider, ErrorBoundary } from '@rollbar/react';
import ReactGA from 'react-ga4';

import agent from '@services/apiService';

import { loadUser, setUserIdleStatus } from '@redux/modules/user';
import { checkAuthStatus } from '@redux/reducers/application/Authentication';
import { REDIRECT, CLAREITY_LOGIN } from '@redux/constants/ActionTypes';

import Header from '@components/general/header/Header';
import ReqImpersonation from '@components/impersonation/ReqImpersonation';
import Sidebar from '@components/general/sideBar/Sidebar';
import RoutesWithCrumbs from '@components/routes/RoutesWithCrumbs';
import routes from '@components/routes/routes';
import Footer from '@components/general/footer/Footer';
import Today from '@components/general/today/Today';
import AppSession from '@components/auth/AppSessionModal';

import usePrevious from '@hooks/usePrevious';
import useAcl from '@hooks/useAcl';
import Idle from 'react-idle';
import useHeartBeat from '@hooks/useHeartBeat';

import P9LogoPrint from '@images/logos/P9LogoBlack.png';

ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_ID);

const rollbarConfig = {
  accessToken: process.env.REACT_APP_ROLLBAR_ACCESS_TOKEN,
  environment: process.env.REACT_APP_ENVIRONMENT,
  server: {
    code_version: process.env.REACT_APP_VERSION,
  },
};

const IDLE_TIMER_TIMEOUT = 1000 * 60 * 5; // 5 minutes

const App = () => {
  useHeartBeat();
  const { redirectTo, clareityUrl } = useSelector(({ utility }) => utility);
  const authChecked = useSelector(({ authentication }) => authentication.authChecked);
  const isLoadingAuth = useSelector(({ authentication }) => authentication.isLoadingAuth);
  const isAuthenticated = useSelector(({ authentication }) => authentication.isAuthenticated);
  const prevIsAuthenticated = usePrevious(isAuthenticated);

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { getMainAcl } = useAcl();

  const [isAppInit, setIsAppInit] = useState(true); // used to control rendering on app start-up
  const [routePaths, setRoutePaths] = useState(null);

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });
  }, []);

  // Check authentication status with every route change.
  useEffect(() => {
    dispatch(checkAuthStatus());
  }, [location]);

  useEffect(() => {
    if (!isAppInit) return;
    if (!authChecked || isLoadingAuth) return;

    if (!isAuthenticated) {
      // redirect to Clareity Login
      dispatch({ type: CLAREITY_LOGIN, payload: agent.Auth.ClareityLogin() });
      return;
    }

    setIsAppInit(false);
  }, [isAppInit, isAuthenticated, isLoadingAuth, authChecked]);

  useEffect(() => {
    if (!authChecked || isLoadingAuth) return;

    if (!prevIsAuthenticated && isAuthenticated) {
      // retrieve main ACL permissions
      getMainAcl({ action: 'view' });
      // load user identity
      dispatch(loadUser());
    }
  }, [isAuthenticated, isLoadingAuth, authChecked]);

  // redirect has been returned and has data,
  useEffect(() => {
    if (redirectTo) {
      history.push(redirectTo);
      dispatch({ type: REDIRECT });
    }
  }, [redirectTo]);

  // follow url returned by API, needs to be in seperate redirect to avoid doubling entries to
  useEffect(() => {
    if (clareityUrl) window.location.href = clareityUrl;
  }, [clareityUrl]);

  // prepare routePaths dictionary object
  useEffect(() => {
    if (routePaths === null) {
      const pathDict = Object.assign({}, ...routes.map(x => ({ [x.breadcrumbPath]: x.name })));
      setRoutePaths(pathDict);
    }
  }, [routePaths]);

  if (isAppInit) return null;

  return (
    <Provider config={rollbarConfig}>
      <ErrorBoundary>
        <Idle
          timeout={IDLE_TIMER_TIMEOUT}
          onChange={({ idle }) => dispatch(setUserIdleStatus(idle))}
          events={[
            'scroll',
            'keydown',
            'mousedown',
            'touchstart',
            'MSPointerDown',
          ]}
        />
        <div className="page-wrapper">
          <div className="page-inner">
            <Sidebar />
            <div className="page-content-wrapper">
              <Header />
              <main id="js-page-content" role="main" className="page-content">
                <img className="d-none d-print-block mb-6" src={P9LogoPrint} alt="P9 Logo" />
                <AppSession />
                <ReqImpersonation />
                <Switch>
                  {routes.map(({ path, Component, id }) => (
                    <Route exact path={path} key={id}>
                      <RoutesWithCrumbs Component={Component} routePathList={routePaths} />
                    </Route>
                  ))}
                </Switch>
                <Today />
              </main>
              <Footer />
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </Provider>
  );
};

export default App;
